/**
 * Secure-context guard for media capture.
 *
 * Camera / microphone / screen sharing (`navigator.mediaDevices.*`) are only exposed by browsers in
 * a SECURE CONTEXT: HTTPS, or http://localhost (and 127.0.0.1). Over plain HTTP on a LAN IP — e.g.
 * http://192.168.x.x:3000 — `navigator.mediaDevices` is `undefined`, so a bare
 * `navigator.mediaDevices.getUserMedia(...)` crashes with the cryptic
 * "Cannot read properties of undefined (reading 'getUserMedia')".
 *
 * These helpers let the media hooks fail with a clear, actionable message instead.
 */

/** True if the browser actually exposes the media-capture API in this context. */
export function hasMediaDevices() {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices;
}

/** True when camera/mic/screen capture can work here (HTTPS or localhost). */
export function isSecureMediaContext() {
  if (typeof window === 'undefined') return false;
  // window.isSecureContext is authoritative; mediaDevices presence corroborates it.
  return !!window.isSecureContext && hasMediaDevices();
}

/** A clear, candidate-facing explanation of why media is blocked + how to fix it. */
export function insecureMediaMessage() {
  const origin =
    (typeof window !== 'undefined' && window.location && window.location.origin) || 'this address';
  return (
    `Camera, microphone and screen sharing only work over a secure connection (https://) ` +
    `or on localhost. This page is open at ${origin}, which the browser treats as insecure, ` +
    `so it blocked media access. Please open the interview using its https:// link to continue.`
  );
}

/**
 * True when this browser/device can capture the screen (getDisplayMedia). Phones and tablets
 * expose camera/mic (getUserMedia) but NOT screen capture, so this is false on mobile even over
 * a perfectly valid HTTPS connection. Use it to tell mobile users to switch to a laptop instead
 * of wrongly blaming the connection.
 */
export function canScreenShare() {
  return hasMediaDevices() && typeof navigator.mediaDevices.getDisplayMedia === 'function';
}

/** Message for when the context is secure but screen capture isn't available (phone/tablet). */
export function screenShareUnsupportedMessage() {
  return (
    `Screen sharing isn't available on phones or tablets. This interview requires sharing your ` +
    `entire screen, so please open the interview link on a laptop or desktop computer ` +
    `(Google Chrome or Microsoft Edge) to continue.`
  );
}
